import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import heroImage from "@/assets/stamped-concrete-orange-essex-county-nj.webp";
import paversVsStampedImage from "@/assets/stamped-concrete-vs-pavers-totowa-nj.webp";

const faqs = [
  {
    question: "What is the downside of stamped concrete?",
    answer:
      "The main downside is cracking, especially in climates with freeze-thaw cycles like North Jersey. Once a stamped slab cracks, repairs are difficult because patches rarely match the original color or pattern. It also needs resealing every two to three years to maintain its appearance, and salt damage during winter can pit the surface and fade the color faster than expected.",
  },
  {
    question: "Which is better, pavers or stamped concrete?",
    answer:
      "Pavers tend to perform better in North Jersey's climate. They flex with ground movement instead of cracking, individual units can be replaced if damaged, and they don't require routine resealing. Stamped concrete can look great initially, but the long-term maintenance and repair limitations make pavers a more practical choice for most homeowners in this area.",
  },
  {
    question: "What is the lifespan of stamped concrete?",
    answer:
      "A well-installed stamped concrete slab can last 15 to 25 years with proper maintenance. That means resealing on schedule and avoiding heavy salt use in winter. In North Jersey, the freeze-thaw cycles tend to shorten that lifespan compared to milder climates. Pavers, by comparison, can last 30 years or more because damaged units are replaceable without affecting the rest of the surface.",
  },
  {
    question: "How often does stamped concrete need to be sealed?",
    answer:
      "Every two to three years. Sealing protects the color and surface from moisture, UV, and salt damage. If you skip a cycle, the surface can start to fade, roughen, and absorb water. That leads to faster cracking in cold weather. It's an ongoing cost and time commitment that many homeowners don't factor in at the start.",
  },
  {
    question: "What is a cheaper alternative to stamped concrete?",
    answer:
      "Concrete pavers can be a cost-competitive alternative, especially when you factor in long-term maintenance. The upfront cost may be similar or slightly higher depending on the material, but you avoid the recurring expense of resealing every few years. And if a repair is needed down the road, replacing a few pavers costs far less than patching or resurfacing a stamped slab.",
  },
];

export default function StampedConcrete() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Stamped Concrete Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Considering stamped concrete in North Jersey? Learn how freeze-thaw cycles affect durability and why homeowners often choose pavers instead. Free estimates." />
        <meta property="og:description" content="Considering stamped concrete in North Jersey? Learn how freeze-thaw cycles affect durability and why homeowners often choose pavers instead. Free estimates." />
        <meta property="og:title" content="Stamped Concrete Installation Totowa, NJ | Masonry Pro" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Stamped Concrete Installation",
            description: "Stamped concrete installation and consultation in North Jersey. Durability guidance for freeze-thaw climates.",
            url: "/concrete-services/stamped-concrete",
            image: "https://masonry-pro.com/og/stamped-concrete.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/stamped-concrete.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/stamped-concrete.jpg" />
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
          <img src={heroImage} alt="Stamped concrete installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
              <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Stamped Concrete</span> Installation in Totowa, NJ Serving North Jersey
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              Considering stamped concrete? Learn how North Jersey's climate affects durability and why many homeowners choose pavers instead.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
              <Button variant="goldOutline" size="xl" asChild>
                <a href="#contact">Get Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
              <p>
                Stamped concrete comes up a lot when homeowners start looking at patio or driveway options. It looks great in photos. The patterns can mimic stone, brick, or slate, and the upfront cost is usually lower than natural materials.
              </p>
              <p>
                But here in North Jersey, there are a few things worth knowing before you commit. The weather puts decorative concrete through a lot. And once it's poured, your options for fixing problems are limited.
              </p>
              <p>
                We get calls about stamped concrete regularly. And honestly, most of those conversations end with homeowners choosing pavers instead. Not because stamped concrete is bad. It just doesn't always hold up the way people expect it to in this climate.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Why Stamped Concrete Can Be Tricky */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Why Stamped Concrete Can Be <span className="text-gradient-gold">Tricky in North Jersey</span>
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
              <p>
                The biggest issue is freeze-thaw cycling. Water gets into the surface, freezes, expands, and cracks the slab. <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link> and <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link> counties can see dozens of freeze-thaw cycles in a single winter. That's a lot of stress on a decorative surface.
              </p>
              <p>
                Once a stamped slab cracks, the repair options aren't great. You can't pull out one section and replace it like you would with a paver. A patch rarely matches the original color or pattern. So a small crack can turn into an eyesore pretty quickly.
              </p>
              <p>
                Sealing is the other thing. Stamped concrete needs to be resealed every two to three years to protect the color and surface. Skip a cycle and the color fades, the surface gets rough, and moisture starts doing damage underneath. That's ongoing maintenance most homeowners don't expect when they sign up for the project.
              </p>
              <p>
                Salt is another factor. A lot of homeowners across <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link> and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties use rock salt or ice melt on their driveways and walkways during winter. That eats away at stamped concrete faster than standard broom-finish slabs. It pits the surface and breaks down the sealer.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Why Homeowners Go With Pavers */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Why Many North Jersey Homeowners Go With <span className="text-gradient-gold">Pavers Instead</span>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                <p>
                  Pavers handle North Jersey weather differently. Each unit sits individually on a compacted base, so when the ground shifts with freeze-thaw, the pavers move with it instead of cracking.
                </p>
                <p>
                  If one paver does crack or stain, you pull it out and drop in a new one. Ten minutes. No color matching issues, no visible patch. That alone is a big deal for homeowners who want something that looks good long-term without constant upkeep.
                </p>
                <p>
                  There's no resealing schedule either. Pavers can be sealed if you want a wet look or extra protection, but they don't require it the way stamped concrete does. Less maintenance over the life of the patio or walkway.
                </p>
                <p>
                  Pattern and color options go further than most people expect. Brick, cobblestone, herringbone, running bond. Natural stone looks without the natural stone price. You can mix colors and patterns in ways that stamped concrete can't match once the mold is set.
                </p>
                <p>
                  That same logic applies to driveways. Paver driveways can flex with ground movement instead of cracking, and a single damaged paver can be swapped out without redoing the whole surface.
                </p>
                <p>
                  We install a lot of paver patios, walkways, and driveways across Bergen, Passaic, Essex, and Morris counties. If you're comparing the two, we're happy to walk you through both options and help you figure out what fits your property and your budget. Give us a call or text and we can set up a time to take a look.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img
                  src={paversVsStampedImage}
                  alt="Stamped concrete vs. pavers at the corporate offices for Masonry Pro Construction Totowa, Passaic County, New Jersey, 07512."
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  width={800}
                  height={800}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-10">
              Pavers vs. Stamped Concrete in <span className="text-gradient-gold">North Jersey</span>
            </h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border bg-primary">
                    <TableHead className="font-bold text-primary-foreground text-base w-[200px]">Factor</TableHead>
                    <TableHead className="font-bold text-primary-foreground text-base">Stamped Concrete</TableHead>
                    <TableHead className="font-bold text-primary-foreground text-base">Pavers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Freeze-thaw durability", "Prone to cracking from repeated cycles", "Individual units flex with ground movement"],
                    ["Repairs", "Patches rarely match the original color or pattern", "Pull out one unit, drop in a new one"],
                    ["Sealing", "Resealing required every 2-3 years", "Optional, not required"],
                    ["Salt/ice melt damage", "Pits the surface and breaks down the sealer", "Minimal impact on paver surfaces"],
                    ["Upfront cost", "Typically lower", "Similar or slightly higher depending on material"],
                    ["Long-term maintenance cost", "Higher due to resealing and limited repair options", "Lower, minimal upkeep needed"],
                    ["Design flexibility", "Patterns set at pour, can't change later", "Mix colors, patterns, and layouts anytime"],
                    ["Lifespan in NJ climate", "15-25 years with proper maintenance", "30+ years with replaceable units"],
                    ["Appearance over time", "Color fades without consistent resealing", "Holds color, weathers naturally"],
                  ].map(([factor, stamped, pavers]) => (
                    <TableRow key={factor} className="border-border">
                      <TableCell className="font-semibold text-foreground">{factor}</TableCell>
                      <TableCell className="text-muted-foreground">{stamped}</TableCell>
                      <TableCell className="text-muted-foreground">{pavers}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </section>

        {/* Category hub backlink */}
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <p className="text-muted-foreground">
                Stamped concrete is one finish within <Link to="/concrete-services" className="text-secondary hover:underline">our full range of concrete services</Link>, and we're happy to talk through what holds up best around here.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
              Frequently Asked <span className="text-gradient-gold">Questions</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {faqs.map((faq) => (
                <div
                  key={faq.question}
                  className="bg-card rounded-xl border border-border p-6 space-y-3"
                >
                  <h3 className="font-semibold text-lg">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <RelatedServices currentCategory="concrete" />

        {/* CTA Section */}
        <CTASection serviceName="stamped concrete" heading="Need a" highlightedHeading="Concrete Estimate?" />
      </main>

      <Footer />
    </>
  );
}
